import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Smartphone, X, Share, Plus, Apple } from 'lucide-react';

/**
 * Always-visible "Install App" button that:
 *  - Triggers the native install prompt on Android Chrome / desktop Chromium.
 *  - Shows step-by-step iOS instructions on iPhone Safari (which lacks beforeinstallprompt).
 *  - Hides itself if the app is already installed (running in standalone mode).
 */
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent || '');
const isAndroid = () => /android/i.test(navigator.userAgent || '');
const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export default function InstallAppButton({ className = '' }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosModal, setShowIosModal] = useState(false);
  const [showAndroidHelp, setShowAndroidHelp] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return;
    }
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const installedHandler = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch {
        /* ignore */
      }
      setDeferredPrompt(null);
      return;
    }
    if (isIos()) {
      setShowIosModal(true);
      return;
    }
    // Android Chrome that hasn't fired the event yet, or other browsers
    setShowAndroidHelp(true);
  };

  if (installed) {
    return (
      <div
        data-testid="app-installed-badge"
        className={`inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-600/10 px-4 py-2 text-sm font-semibold text-emerald-300 ${className}`}
      >
        <Smartphone className="h-4 w-4" />
        App installed
      </div>
    );
  }

  return (
    <>
      <Button
        data-testid="install-app-btn"
        onClick={handleClick}
        className={`bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold px-8 py-5 text-lg rounded-full shadow-lg shadow-purple-500/40 transition-all hover:scale-105 ${className}`}
      >
        <Download className="w-5 h-5 mr-2" />
        Install the App
      </Button>

      {/* iOS instructions modal */}
      {showIosModal && (
        <div
          className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/70 backdrop-blur-sm p-4 sm:items-center"
          onClick={() => setShowIosModal(false)}
        >
          <div
            data-testid="ios-install-modal"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-purple-500/40 bg-zinc-900 p-6 text-white shadow-2xl"
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Apple className="h-6 w-6 text-zinc-300" />
                <h3 className="text-lg font-bold">Install on iPhone</h3>
              </div>
              <button
                data-testid="ios-install-modal-close"
                onClick={() => setShowIosModal(false)}
                className="rounded-full p-1 text-zinc-400 hover:bg-zinc-800 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mb-4 text-sm text-zinc-300">
              Apple doesn't let websites install apps automatically — you'll do it in 3 quick taps:
            </p>
            <ol className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-purple-600 text-sm font-bold">1</span>
                <p className="text-sm leading-relaxed text-zinc-200">
                  Tap the <Share className="inline h-4 w-4 align-text-bottom" />{' '}
                  <span className="font-semibold">Share</span> button at the bottom of Safari.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-purple-600 text-sm font-bold">2</span>
                <p className="text-sm leading-relaxed text-zinc-200">
                  Scroll and tap <Plus className="inline h-4 w-4 align-text-bottom" />{' '}
                  <span className="font-semibold">Add to Home Screen</span>.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-purple-600 text-sm font-bold">3</span>
                <p className="text-sm leading-relaxed text-zinc-200">
                  Tap <span className="font-semibold">Add</span> in the top-right corner. NightBuZz will appear on your home screen.
                </p>
              </li>
            </ol>
            <Button
              onClick={() => setShowIosModal(false)}
              className="mt-5 w-full bg-purple-600 hover:bg-purple-500"
              data-testid="ios-install-modal-got-it"
            >
              Got it
            </Button>
          </div>
        </div>
      )}

      {/* Android / other-browser fallback modal */}
      {showAndroidHelp && (
        <div
          className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/70 backdrop-blur-sm p-4 sm:items-center"
          onClick={() => setShowAndroidHelp(false)}
        >
          <div
            data-testid="android-install-modal"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-purple-500/40 bg-zinc-900 p-6 text-white shadow-2xl"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">Install on Android</h3>
              <button
                data-testid="android-install-modal-close"
                onClick={() => setShowAndroidHelp(false)}
                className="rounded-full p-1 text-zinc-400 hover:bg-zinc-800 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mb-3 text-sm text-zinc-300">
              {isAndroid()
                ? 'Your browser hasn\'t offered an install prompt yet. Try this:'
                : 'On a mobile browser (Chrome, Edge, or similar):'}
            </p>
            <ol className="space-y-3 text-sm text-zinc-200">
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-purple-600 text-sm font-bold">1</span>
                Tap the <span className="font-semibold">⋮ menu</span> (3 dots in the top-right).
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-purple-600 text-sm font-bold">2</span>
                Tap <span className="font-semibold">"Install app"</span> or{' '}
                <span className="font-semibold">"Add to Home screen"</span>.
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-purple-600 text-sm font-bold">3</span>
                Confirm. NightBuZz will appear on your home screen like any other app.
              </li>
            </ol>
            <Button
              onClick={() => setShowAndroidHelp(false)}
              className="mt-5 w-full bg-purple-600 hover:bg-purple-500"
              data-testid="android-install-modal-got-it"
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
